import React, { useEffect, useMemo, useState } from 'react';
import { CaretLeft, CaretRight } from '@phosphor-icons/react';

export const HELP_PAGE_SIZE = 10;

/**
 * Client-side paging. Goes back to page 1 whenever `resetKey` changes
 * (e.g. a new search or filter), and stays in range when items shrink.
 */
export function usePaged(items, resetKey, pageSize = HELP_PAGE_SIZE) {
  const [page, setPage] = useState(1);
  const pageCount = Math.max(1, Math.ceil(items.length / pageSize));

  useEffect(() => setPage(1), [resetKey]);
  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const pageItems = useMemo(
    () => items.slice((page - 1) * pageSize, page * pageSize),
    [items, page, pageSize],
  );
  return { page, pageCount, pageItems, setPage, total: items.length, pageSize };
}

function HelpPagination({ page, pageCount, setPage, total, pageSize }) {
  if (pageCount <= 1) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);

  // First, last, current ±1, with gaps.
  const pages = [];
  for (let p = 1; p <= pageCount; p += 1) {
    if (p === 1 || p === pageCount || Math.abs(p - page) <= 1) pages.push(p);
    else if (pages[pages.length - 1] !== '…') pages.push('…');
  }

  const btn = (active) => ({
    minWidth: '28px', padding: '4px 8px', fontSize: '12px',
    ...(active ? {} : { background: 'white' }),
  });

  return (
    <div
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap', marginTop: '10px' }}
      data-testid="help-pagination"
    >
      <span style={{ fontSize: '12px', color: '#6B7280' }}>{from}–{to} of {total}</span>
      <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
        <button
          type="button"
          className="btn-secondary"
          style={{ ...btn(false), display: 'flex', alignItems: 'center' }}
          disabled={page === 1}
          onClick={() => setPage(page - 1)}
          aria-label="Previous page"
        >
          <CaretLeft size={12} />
        </button>
        {pages.map((p, i) => (p === '…' ? (
          <span key={`gap-${i}`} style={{ fontSize: '12px', color: '#9CA3AF', padding: '0 2px' }}>…</span>
        ) : (
          <button
            key={p}
            type="button"
            className={p === page ? 'btn-primary' : 'btn-secondary'}
            style={btn(p === page)}
            onClick={() => setPage(p)}
            aria-current={p === page ? 'page' : undefined}
          >
            {p}
          </button>
        )))}
        <button
          type="button"
          className="btn-secondary"
          style={{ ...btn(false), display: 'flex', alignItems: 'center' }}
          disabled={page === pageCount}
          onClick={() => setPage(page + 1)}
          aria-label="Next page"
        >
          <CaretRight size={12} />
        </button>
      </div>
    </div>
  );
}

export default HelpPagination;
